import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import './PropertySwipeHint.css';

const PropertySwipeHint = ({ workspaceRef, resultsRef, paused = false }) => {
  const [visible, setVisible] = useState(false);
  const dismissed = useRef(false);
  const touchStart = useRef(null);

  useEffect(() => {
    const workspace = workspaceRef.current;
    const results = resultsRef.current;
    if (!workspace || !results) return undefined;
    const mobile = window.matchMedia('(max-width: 820px)');
    let frame;
    const update = () => {
      const viewport = workspace.getBoundingClientRect();
      const panel = results.getBoundingClientRect();
      const visibleWidth = Math.max(0, Math.min(viewport.right, panel.right) - Math.max(viewport.left, panel.left));
      const tilesInView = viewport.width > 0 && visibleWidth >= viewport.width * 0.55;
      if (mobile.matches && tilesInView) dismissed.current = true;
      setVisible(mobile.matches && !paused && !dismissed.current && !tilesInView);
    };
    const scheduleUpdate = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(update);
    };
    update();
    workspace.addEventListener('scroll', scheduleUpdate, { passive: true });
    window.addEventListener('resize', scheduleUpdate);
    mobile.addEventListener('change', scheduleUpdate);
    return () => {
      cancelAnimationFrame(frame);
      workspace.removeEventListener('scroll', scheduleUpdate);
      window.removeEventListener('resize', scheduleUpdate);
      mobile.removeEventListener('change', scheduleUpdate);
    };
  }, [workspaceRef, resultsRef, paused]);

  const dismiss = () => { dismissed.current = true; setVisible(false); };
  const showProperties = () => {
    const workspace = workspaceRef.current;
    const results = resultsRef.current;
    if (!workspace || !results) return;
    workspace.scrollTo({
      left: workspace.scrollLeft + results.getBoundingClientRect().left - workspace.getBoundingClientRect().left,
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'
    });
    dismiss();
  };

  if (!visible) return null;
  return createPortal(
    <div className="property-swipe-hint">
      <button
        className="property-swipe-hint-action"
        type="button"
        onClick={showProperties}
        onTouchStart={event => { touchStart.current = event.touches[0]; }}
        onTouchCancel={() => { touchStart.current = null; }}
        onTouchEnd={event => {
          const start = touchStart.current;
          const end = event.changedTouches[0];
          touchStart.current = null;
          if (start && end && Math.abs(end.clientX - start.clientX) > 36 && Math.abs(end.clientX - start.clientX) > Math.abs(end.clientY - start.clientY) * 1.25) showProperties();
        }}
        aria-label="Show matching properties"
      >
        <span><small role="status">Properties ready</small><strong>Swipe</strong></span>
        <svg className="property-swipe-hint-arrow" width="48" height="32" viewBox="0 0 48 32" fill="none" aria-hidden="true">
          <path d="M4 16h38M29 3l13 13-13 13" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
      <button className="property-swipe-hint-close" type="button" onClick={dismiss} aria-label="Dismiss swipe hint">×</button>
    </div>,
    document.body
  );
};

export default PropertySwipeHint;
